import { useState } from "react";
import { AnimatePresence } from "framer-motion";
import { Compass, ShieldCheck, PhoneCall, Sparkles } from "lucide-react";
import PageHeader from "../components/PageHeader";
import Reveal from "../components/Reveal";
import PackageCard from "../components/PackageCard";
import PackageModal from "../components/PackageModal";
import { INTERNATIONAL_PACKAGES } from "../data/packages";

const VIBES = ["All Vibes", ...new Set(INTERNATIONAL_PACKAGES.map((p) => p.region))];

const GUARANTEES = [
  { icon: Compass, title: "Visa & Documentation", desc: "End-to-end guidance on visas, permits and travel insurance." },
  { icon: ShieldCheck, title: "Handpicked Stays", desc: "Every hotel, ryokan and lodge personally vetted by our team." },
  { icon: PhoneCall, title: "24/7 On-Trip Support", desc: "A curator a call away, in your timezone, for the whole journey." },
  { icon: Sparkles, title: "Small Groups Only", desc: "International departures capped at 14 travellers, always." },
];

export default function InternationalPackages() {
  const [vibe, setVibe] = useState("All Vibes");
  const [sel, setSel] = useState(null);

  const list = vibe === "All Vibes" ? INTERNATIONAL_PACKAGES : INTERNATIONAL_PACKAGES.filter((p) => p.region === vibe);

  return (
    <div data-testid="page-international-packages">
      <PageHeader
        testid="international-header"
        eyebrow="International Tour Packages"
        title="An odyssey across"
        accent="continents"
        description="Northern lights, alpine rails, temple mornings and tropical coastlines — the world's great journeys, curated to Cosmos standards."
      />

      <section className="mx-auto max-w-7xl px-6 pb-24 md:pb-32">
        <Reveal>
          <div className="flex flex-wrap gap-2.5" data-testid="vibe-filter">
            {VIBES.map((v) => (
              <button
                key={v}
                data-testid={`vibe-chip-${v.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                onClick={() => setVibe(v)}
                className={`rounded-full border px-5 py-2.5 text-xs font-semibold tracking-wide transition-all duration-300 ${
                  vibe === v
                    ? "border-aurora bg-aurora text-ink"
                    : "border-white/15 text-slate-300 hover:border-aurora/50 hover:text-aurora"
                }`}
              >
                {v}
              </button>
            ))}
          </div>
        </Reveal>

        <div className="mt-12 grid gap-7 sm:grid-cols-2 lg:grid-cols-3" data-testid="international-grid">
          {list.map((pkg, i) => (
            <Reveal key={pkg.name} delay={(i % 3) * 0.08}>
              <PackageCard pkg={pkg} onOpen={setSel} testid={`international-card-${pkg.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} />
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-20">
          <div className="grid gap-8 rounded-[2rem] border border-white/10 bg-obsidian/60 p-8 md:grid-cols-4 md:p-12" data-testid="concierge-panel">
            {GUARANTEES.map((g) => (
              <div key={g.title}>
                <g.icon className="h-6 w-6 text-aurora" />
                <h3 className="mt-4 font-display text-base font-bold text-slate-50">{g.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{g.desc}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      <AnimatePresence>
        {sel && <PackageModal pkg={sel} onClose={() => setSel(null)} tripType="International Package" />}
      </AnimatePresence>
    </div>
  );
}
